<template>
  <div class="sound-tag-search-editor" v-loading="loading">
    <section class="editor-section tag-editor-section">
      <div class="editor-heading">
        <div>
          <h3>标准标签</h3>
          <p>从不同角度描述音效。标签可重复使用，保存时会自动补齐上级标签。</p>
        </div>
        <el-radio-group v-model="editorMode" size="mini">
          <el-radio-button label="guided">引导模式</el-radio-button>
          <el-radio-button label="quick">快速模式</el-radio-button>
        </el-radio-group>
      </div>

      <el-alert
        v-if="!soundId"
        type="info"
        :closable="false"
        show-icon
        title="这里的选择会在音效创建成功后一起保存。"
      ></el-alert>

      <template v-if="activeGroups.length">
        <div v-if="editorMode === 'guided'" class="guided-editor">
          <div class="guide-progress">
            <div>
              <strong>引导进度 {{ guideGroupIndex + 1 }} / {{ activeGroups.length }}</strong>
              <span>不确定时可以跳过，也可以随时切换到快速模式。</span>
            </div>
            <el-progress
              :percentage="guideProgress"
              :show-text="false"
              :stroke-width="7"
            ></el-progress>
          </div>

          <div v-if="currentGuideGroup" class="guide-card">
            <div class="guide-question">
              <div>
                <span class="guide-step">当前思考方向</span>
                <h4>{{ currentGuideGroup.name }}</h4>
                <p>{{ guidePrompt(currentGuideGroup) }}</p>
              </div>
              <span class="selected-count">
                已选 {{ groupSelectedCount(currentGuideGroup) }} 个
              </span>
            </div>

            <SoundTagGroupPicker
              v-model="tagIds"
              :group="currentGuideGroup"
              :disabled="savingTags"
              allow-create
              @create="openCreateTagDialog"
            ></SoundTagGroupPicker>

            <div class="guide-actions">
              <el-button
                size="small"
                :disabled="guideGroupIndex === 0"
                @click="previousGuideGroup"
              >
                上一组
              </el-button>
              <el-button
                v-if="groupSelectedCount(currentGuideGroup)"
                size="small"
                type="text"
                class="clear-group-button"
                @click="clearCurrentGroup"
              >
                清空本组
              </el-button>
              <span class="guide-spacer"></span>
              <el-button size="small" @click="skipGuideGroup">暂时跳过</el-button>
              <el-button type="primary" size="small" @click="nextGuideGroup">
                {{ guideGroupIndex === activeGroups.length - 1 ? '查看全部结果' : '下一组' }}
              </el-button>
            </div>
          </div>
        </div>

        <div v-else class="quick-editor">
          <div class="quick-tip">
            <i class="el-icon-lightning"></i>
            按标签组快速浏览；有已选标签的组会显示数量。
          </div>
          <el-collapse v-model="expandedGroupIds">
            <el-collapse-item
              v-for="group in activeGroups"
              :key="group.id"
              :name="String(group.id)"
            >
              <template slot="title">
                <span class="collapse-title">{{ group.name }}</span>
                <el-tag
                  v-if="groupSelectedCount(group)"
                  size="mini"
                  effect="plain"
                >
                  已选 {{ groupSelectedCount(group) }}
                </el-tag>
                <span class="collapse-description">{{ group.description }}</span>
              </template>
              <SoundTagGroupPicker
                v-model="tagIds"
                :group="group"
                :disabled="savingTags"
                allow-create
                @create="openCreateTagDialog"
              ></SoundTagGroupPicker>
            </el-collapse-item>
          </el-collapse>
        </div>
      </template>
      <el-empty v-else :image-size="70" description="暂时没有可选择的标签"></el-empty>

      <div class="selected-summary">
        <div class="selected-summary-heading">
          <div>
            <strong>已选择标签与关联度</strong>
            <p>可输入 0–100 的任意整数；分数越高，搜索排序和客户端展示越靠前。</p>
            <p class="relevance-scale-help">
              90–100 核心，75–89 重要，50–74 相关，25–49 弱相关，0–24 极弱。
            </p>
          </div>
          <span>{{ selectedTagObjects.length }} 个</span>
        </div>
        <div v-if="selectedTagObjects.length" class="selected-tags">
          <div
            v-for="tag in selectedTagObjects"
            :key="tag.id"
            class="selected-tag-item"
          >
            <div class="selected-tag-name">
              <small>{{ tag.group_name }}</small>
              <span>{{ tag.name }}</span>
            </div>
            <div class="relevance-editor">
              <span
                class="relevance-level"
                :class="`relevance-level--${relevanceLevel(tagRelevanceScore(tag.id)).key}`"
              >
                {{ relevanceLevel(tagRelevanceScore(tag.id)).label }}
              </span>
              <el-input-number
                :value="tagRelevanceScore(tag.id)"
                :min="0"
                :max="100"
                :step="5"
                :precision="0"
                size="mini"
                controls-position="right"
                class="relevance-input"
                @change="setTagRelevanceScore(tag.id, $event)"
              ></el-input-number>
            </div>
            <el-button
              type="text"
              icon="el-icon-delete"
              class="remove-tag-button"
              title="移除这个标签"
              @click="removeTag(tag.id)"
            ></el-button>
          </div>
        </div>
        <span v-else class="empty-copy">还没有选择标签，可以从上面的标签组开始。</span>
      </div>

      <div class="section-save-row">
        <span v-if="!soundId">创建音效时自动保存</span>
        <span v-else>标签修改需要单独保存</span>
        <el-button
          v-if="soundId"
          type="primary"
          :loading="savingTags"
          @click="saveTags()"
        >
          保存标签
        </el-button>
      </div>
    </section>

    <section class="editor-section">
      <div class="editor-heading">
        <div>
          <h3>这个音效的搜索词</h3>
          <p>只用于当前音效。一次可输入多个词，用逗号或换行分隔。</p>
        </div>
        <span class="selected-count">{{ searchTerms.length }} 个</span>
      </div>

      <el-input
        v-model="newSearchTerms"
        type="textarea"
        :rows="3"
        maxlength="1000"
        show-word-limit
        placeholder="例如：要什么自行车，本山大叔，卖拐"
      ></el-input>
      <div class="term-add-row">
        <span>系统会自动去重，每个搜索词不少于两个字符。</span>
        <el-button
          type="primary"
          plain
          :loading="savingTerms"
          @click="addSearchTerms"
        >
          {{ soundId ? '添加搜索词' : '加入待保存列表' }}
        </el-button>
      </div>

      <div v-if="searchTerms.length" class="term-list">
        <div v-for="term in searchTerms" :key="term.id" class="term-item">
          <div class="term-main">
            <span>{{ term.term }}</span>
            <el-tag size="mini" :type="termSourceType(term.source)">
              {{ termSourceLabel(term.source) }}
            </el-tag>
          </div>
          <el-button
            type="text"
            class="danger-text"
            icon="el-icon-delete"
            @click="removeSearchTerm(term)"
          >
            删除
          </el-button>
        </div>
      </div>
      <el-empty v-else :image-size="64" description="还没有独立搜索词"></el-empty>
    </section>

    <section v-if="showLegacy" class="editor-section legacy-section">
      <div class="editor-heading">
        <div>
          <h3>旧扩展关键词（只读）</h3>
          <p>为了兼容旧客户端，这里的原始内容暂时保留。</p>
        </div>
      </div>
      <div class="legacy-description">
        {{ loadedSound.discription || '没有旧扩展关键词' }}
      </div>
    </section>

    <SoundTagDialog
      :visible.sync="tagDialogVisible"
      :groups="availableGroups"
      :default-group-id="tagDialogGroupId"
      :default-name="tagDialogName"
      @saved="handleTagCreated"
    ></SoundTagDialog>
  </div>
</template>

<script>
import apis from '@/api/index'
import SoundTagDialog from './SoundTagDialog.vue'
import SoundTagGroupPicker from './SoundTagGroupPicker.vue'
import {
  getSoundTagPreferences,
  SOUND_TAG_PREFERENCES_CHANGE_EVENT
} from '@/lib/soundTagPreferences'

export default {
  name: 'SoundTagSearchEditor',
  components: {
    SoundTagDialog,
    SoundTagGroupPicker
  },
  props: {
    soundId: {
      type: [Number, String],
      default: null
    },
    groups: {
      type: Array,
      default: () => []
    },
    showLegacy: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      internalGroups: [],
      loadedSound: {},
      tagIds: [],
      tagRelevanceScores: {},
      searchTerms: [],
      newSearchTerms: '',
      loading: false,
      savingTags: false,
      savingTerms: false,
      editorMode: getSoundTagPreferences().defaultEditorMode,
      guideGroupIndex: 0,
      expandedGroupIds: [],
      tagDialogVisible: false,
      tagDialogGroupId: null,
      tagDialogName: '',
      draftTermSequence: 0,
      loadSequence: 0
    }
  },
  computed: {
    /**
     * 优先使用组件内部刷新后的标签目录，否则使用父组件传入目录。
     *
     * @returns {Array}
     */
    availableGroups() {
      return this.internalGroups.length ? this.internalGroups : this.groups
    },
    /**
     * 返回应在编辑器中出现的标签组。
     *
     * 停用组通常隐藏，但只要其中存在当前音效已选标签就继续显示，避免
     * 管理员在编辑其他字段时无意丢掉历史标签。
     *
     * @returns {Array}
     */
    activeGroups() {
      const selected = new Set(this.tagIds.map(Number))
      return this.availableGroups.filter(group => {
        if (Number(group.status) === 1) return true
        return (group.tags || []).some(tag => selected.has(Number(tag.id)))
      })
    },
    /**
     * 返回引导模式当前步骤的标签组。
     *
     * @returns {Object|null}
     */
    currentGuideGroup() {
      return this.activeGroups[this.guideGroupIndex] || null
    },
    /**
     * 计算引导模式进度百分比。
     *
     * @returns {number}
     */
    guideProgress() {
      if (!this.activeGroups.length) return 0
      return Math.round(((this.guideGroupIndex + 1) / this.activeGroups.length) * 100)
    },
    /**
     * 将分组标签扁平化，并附带组名用于已选标签摘要。
     *
     * @returns {Array}
     */
    allTagObjects() {
      const result = []
      this.availableGroups.forEach(group => {
        const tags = group.tags || []
        tags.forEach(tag => {
          result.push({ ...tag, group_name: group.name })
        })
      })
      return result
    },
    /**
     * 返回当前音效已经选择的完整标签对象。
     *
     * 编辑过程中保持标签位置稳定，避免管理员调分后当前行突然移动；
     * 客户端和服务端结果仍会按照最终保存的关联度排序。
     *
     * @returns {Array}
     */
    selectedTagObjects() {
      const selected = new Set(this.tagIds.map(Number))
      return this.allTagObjects.filter(tag => selected.has(Number(tag.id)))
    },
    /**
     * 汇总标签和搜索词两个独立保存状态。
     *
     * @returns {boolean}
     */
    isSaving() {
      return this.savingTags || this.savingTerms
    }
  },
  watch: {
    /**
     * 音效 ID 变化时重新加载对应的标签与搜索词。
     *
     * @returns {void}
     */
    soundId: {
      immediate: true,
      handler() {
        this.loadEditor()
      }
    },
    /**
     * 父组件标签目录变化时更新内部目录并恢复合理展开状态。
     *
     * @param {Array} value 最新标签组
     * @returns {void}
     */
    groups: {
      deep: true,
      handler(value) {
        if (this.internalGroups.length && value.length) {
          this.internalGroups = value
        }
        this.ensureGuidePosition()
        this.expandSelectedGroups()
      }
    },
    /**
     * 切换到快速模式时自动展开包含已选标签的组。
     *
     * @param {string} mode 编辑模式
     * @returns {void}
     */
    editorMode(mode) {
      if (mode === 'quick') this.expandSelectedGroups()
    },
    /**
     * 把局部保存状态通知外层页面，便于统一控制离开提示。
     *
     * @param {boolean} value 是否正在保存
     * @returns {void}
     */
    isSaving(value) {
      this.$emit('saving-change', value)
    }
  },
  mounted() {
    window.addEventListener(
      SOUND_TAG_PREFERENCES_CHANGE_EVENT,
      this.handlePreferencesChanged
    )
  },
  beforeDestroy() {
    window.removeEventListener(
      SOUND_TAG_PREFERENCES_CHANGE_EVENT,
      this.handlePreferencesChanged
    )
  },
  methods: {
    /**
     * 加载标签目录以及当前音效的标签、搜索词详情。
     *
     * loadSequence 会丢弃切换音效时较早请求的迟到响应，避免旧数据覆盖
     * 当前音效；新增页没有 soundId 时只加载目录并保留本地草稿。
     *
     * @returns {Promise<void>}
     */
    async loadEditor() {
      const sequence = ++this.loadSequence
      this.loading = true
      try {
        const requests = []
        if (!this.groups.length) requests.push(apis.tag.catalog())
        if (Number(this.soundId) > 0) {
          requests.push(apis.tag.soundDetail({ sound_id: Number(this.soundId) }))
        }
        const responses = await Promise.all(requests)
        if (sequence !== this.loadSequence) return

        let responseIndex = 0
        if (!this.groups.length) {
          const catalogResponse = responses[responseIndex++]
          if (catalogResponse.data.code !== 1) {
            throw new Error(catalogResponse.data.msg || '标签目录加载失败')
          }
          this.internalGroups = (catalogResponse.data.data || {}).groups || []
        }
        if (Number(this.soundId) > 0) {
          const detailResponse = responses[responseIndex]
          if (detailResponse.data.code !== 1) {
            throw new Error(detailResponse.data.msg || '音效标签加载失败')
          }
          const detail = detailResponse.data.data || {}
          this.loadedSound = detail.sound || {}
          this.tagIds = (detail.tag_ids || []).map(Number)
          this.tagRelevanceScores = this.buildTagRelevanceMap(detail.relations)
          this.searchTerms = detail.search_terms || []
        } else {
          this.loadedSound = {}
          this.tagIds = []
          this.tagRelevanceScores = {}
          this.searchTerms = []
          this.newSearchTerms = ''
        }
        this.ensureGuidePosition()
        this.expandSelectedGroups()
      } catch (error) {
        this.$message.error(String(error.message || error || '整理信息加载失败'))
      } finally {
        if (sequence === this.loadSequence) this.loading = false
      }
    },
    /**
     * 响应左下角系统设置的标签编辑模式变化。
     *
     * @param {CustomEvent} event 偏好变化事件
     * @returns {void}
     */
    handlePreferencesChanged(event) {
      const preferences = event && event.detail
        ? event.detail
        : getSoundTagPreferences()
      this.editorMode = preferences.defaultEditorMode
    },
    /**
     * 将引导步骤限制在当前可用标签组范围内。
     *
     * @returns {void}
     */
    ensureGuidePosition() {
      if (!this.activeGroups.length) {
        this.guideGroupIndex = 0
        return
      }
      this.guideGroupIndex = Math.min(
        Math.max(this.guideGroupIndex, 0),
        this.activeGroups.length - 1
      )
    },
    /**
     * 在快速模式中展开包含已选标签的组。
     *
     * 第一次进入且没有已选标签时默认展开第一组，让新用户有明确起点。
     *
     * @returns {void}
     */
    expandSelectedGroups() {
      const selected = new Set(this.tagIds.map(Number))
      const selectedGroups = this.activeGroups
        .filter(group => (group.tags || []).some(tag => selected.has(Number(tag.id))))
        .map(group => String(group.id))
      if (selectedGroups.length) {
        this.expandedGroupIds = Array.from(new Set([
          ...this.expandedGroupIds,
          ...selectedGroups
        ]))
      } else if (!this.expandedGroupIds.length && this.activeGroups.length) {
        this.expandedGroupIds = [String(this.activeGroups[0].id)]
      }
    },
    /**
     * 返回标签组的引导说明，没有人工描述时生成通俗提示。
     *
     * @param {Object} group 标签组
     * @returns {string}
     */
    guidePrompt(group) {
      if (group.description) return group.description
      return `想一想这个音效是否需要添加“${group.name}”方面的标签。`
    },
    /**
     * 统计指定标签组已经选择的标签数量。
     *
     * @param {Object} group 标签组
     * @returns {number}
     */
    groupSelectedCount(group) {
      const selected = new Set(this.tagIds.map(Number))
      return (group.tags || []).filter(tag => selected.has(Number(tag.id))).length
    },
    /**
     * 返回上一个引导步骤。
     *
     * @returns {void}
     */
    previousGuideGroup() {
      if (this.guideGroupIndex > 0) this.guideGroupIndex -= 1
    },
    /**
     * 暂时跳过当前标签组，选择结果保持不变。
     *
     * @returns {void}
     */
    skipGuideGroup() {
      this.moveToNextGuideGroup()
    },
    /**
     * 完成当前标签组并进入下一步。
     *
     * @returns {void}
     */
    nextGuideGroup() {
      this.moveToNextGuideGroup()
    },
    /**
     * 推进引导步骤；最后一组完成后自动切换到快速总览。
     *
     * @returns {void}
     */
    moveToNextGuideGroup() {
      if (this.guideGroupIndex < this.activeGroups.length - 1) {
        this.guideGroupIndex += 1
        return
      }
      this.editorMode = 'quick'
    },
    /**
     * 经用户确认后清空当前标签组的全部选择。
     *
     * @returns {Promise<void>}
     */
    async clearCurrentGroup() {
      const group = this.currentGuideGroup
      if (!group || !this.groupSelectedCount(group)) return
      try {
        await this.$confirm(
          `确定清空“${group.name}”中已经选择的标签吗？`,
          '清空本组',
          { confirmButtonText: '确定清空', cancelButtonText: '取消', type: 'warning' }
        )
        const groupIds = new Set((group.tags || []).map(tag => Number(tag.id)))
        this.tagIds = this.tagIds.filter(id => !groupIds.has(Number(id)))
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(String(error || '清空失败'))
        }
      }
    },
    /**
     * 从当前选择中移除一个标签。
     *
     * @param {number|string} tagId 标签 ID
     * @returns {void}
     */
    removeTag(tagId) {
      this.tagIds = this.tagIds.filter(id => Number(id) !== Number(tagId))
      this.$delete(this.tagRelevanceScores, Number(tagId))
    },
    /**
     * 把接口返回的标签关系整理为便于页面编辑的关联度映射。
     *
     * @param {Array} relations 音效标签关系列表
     * @returns {Object} tag_id => relevance_score
     */
    buildTagRelevanceMap(relations) {
      const result = {}
      ;(relations || []).forEach(relation => {
        const tagId = Number(relation.tag_id)
        if (!tagId) return
        result[tagId] = Math.max(0, Math.min(100, Number(relation.relevance_score) || 0))
      })
      return result
    },
    /**
     * 返回标签当前的语义关联度。管理员新选的标签默认按“相关”处理，
     * 后续可以明确调整为核心、重要或弱相关。
     *
     * @param {number|string} tagId 标签 ID
     * @returns {number}
     */
    tagRelevanceScore(tagId) {
      const value = this.tagRelevanceScores[Number(tagId)]
      return Number.isFinite(Number(value)) ? Number(value) : 60
    },
    /**
     * 把连续的 0–100 分数转换为便于工作人员理解的语义提示。
     *
     * 等级只负责解释，不限制输入值；例如 91、95、98 都属于“核心”，
     * 但仍能在搜索和展示排序中精细区分先后。
     *
     * @param {number|string} score 语义关联度
     * @returns {{key: string, label: string}} 展示样式和中文说明
     */
    relevanceLevel(score) {
      const value = Math.max(0, Math.min(100, Number(score) || 0))
      if (value >= 90) return { key: 'core', label: '核心' }
      if (value >= 75) return { key: 'important', label: '重要' }
      if (value >= 50) return { key: 'related', label: '相关' }
      if (value >= 25) return { key: 'weak', label: '弱相关' }
      return { key: 'minimal', label: '极弱' }
    },
    /**
     * 更新单个标签的语义关联度，使用 Vue.set 保证旧版 Vue 能正确刷新。
     *
     * @param {number|string} tagId 标签 ID
     * @param {number|string} score 关联度
     * @returns {void}
     */
    setTagRelevanceScore(tagId, score) {
      const safeScore = Math.max(0, Math.min(100, Number(score) || 0))
      this.$set(this.tagRelevanceScores, Number(tagId), safeScore)
    },
    /**
     * 生成保存接口需要的关联度映射，只提交当前仍被选中的标签。
     *
     * @returns {Object} tag_id => relevance_score
     */
    buildTagRelevancePayload() {
      const result = {}
      this.tagIds.forEach(tagId => {
        result[Number(tagId)] = this.tagRelevanceScore(tagId)
      })
      return result
    },
    /**
     * 打开通用标签弹窗并预填当前组和搜索词。
     *
     * @param {Object} context 包含 group 和 keyword
     * @returns {void}
     */
    openCreateTagDialog({ group, keyword }) {
      this.tagDialogGroupId = Number(group.id)
      this.tagDialogName = String(keyword || '').trim()
      this.tagDialogVisible = true
    },
    /**
     * 新标签创建后刷新目录并立即选中新标签。
     *
     * 单选组会先移除同组旧选择，确保页面草稿符合组规则。
     *
     * @param {Object} tag 新创建的标签
     * @returns {Promise<void>}
     */
    async handleTagCreated(tag) {
      try {
        const response = await apis.tag.catalog()
        if (response.data.code !== 1) {
          throw new Error(response.data.msg || '标签目录刷新失败')
        }
        const groups = (response.data.data || {}).groups || []
        this.internalGroups = groups
        const group = groups.find(item => Number(item.id) === Number(tag.group_id))
        let nextTagIds = this.tagIds.map(Number)
        if (group && Number(group.allow_multiple) !== 1) {
          const groupTagIds = new Set((group.tags || []).map(item => Number(item.id)))
          nextTagIds = nextTagIds.filter(id => !groupTagIds.has(id))
        }
        nextTagIds.push(Number(tag.id))
        this.tagIds = Array.from(new Set(nextTagIds))
        this.expandedGroupIds = Array.from(new Set([
          ...this.expandedGroupIds,
          String(tag.group_id)
        ]))
        this.$emit('catalog-changed', groups)
      } catch (error) {
        this.$message.warning(
          `新标签已创建，但待选内容刷新失败，请稍后刷新页面：${String(error.message || error)}`
        )
      }
    },
    /**
     * 保存当前音效的完整标签关系。
     *
     * @param {number|string} soundId 音效 ID
     * @param {boolean} silent 新增页联动保存时是否隐藏局部提示
     * @returns {Promise<boolean>}
     */
    async saveTags(soundId = this.soundId, silent = false) {
      const id = Number(soundId)
      if (!id) return false
      this.savingTags = true
      try {
        const response = await apis.tag.saveSoundTags({
          sound_id: id,
          tag_ids: this.tagIds,
          tag_relevances: this.buildTagRelevancePayload()
        })
        if (response.data.code !== 1) {
          throw new Error(response.data.msg || '标签保存失败')
        }
        this.tagIds = ((response.data.data || {}).tag_ids || []).map(Number)
        this.tagRelevanceScores = {
          ...(response.data.data || {}).tag_relevances
        }
        if (!silent) this.$message.success('音效标签已保存')
        this.$emit('changed', { soundId: id, type: 'tags' })
        return true
      } catch (error) {
        if (!silent) {
          this.$message.error(String(error.message || error || '标签保存失败'))
          return false
        }
        throw error
      } finally {
        this.savingTags = false
      }
    },
    /**
     * 将逗号、顿号、分号或换行分隔的输入整理成去重搜索词。
     *
     * 每次最多接收 50 个且至少两个字符，前端限制用于及时反馈，服务端仍会
     * 再次校验，不能把这里当作安全边界。
     *
     * @param {string} value 用户输入
     * @returns {Array<string>}
     */
    normalizeInputTerms(value) {
      const result = []
      const seen = new Set()
      String(value || '').split(/[,，、;；\r\n]+/).forEach(item => {
        const term = item.trim()
        const normalized = term.toLowerCase().replace(/\s+/g, '')
        if (term.length < 2 || !normalized || seen.has(normalized)) return
        seen.add(normalized)
        result.push(term)
      })
      return result.slice(0, 50)
    },
    /**
     * 在新增页加入本地待保存搜索词，或在编辑页立即写入数据库。
     *
     * @returns {Promise<void>}
     */
    async addSearchTerms() {
      const terms = this.normalizeInputTerms(this.newSearchTerms)
      if (!terms.length) {
        this.$message.warning('请输入至少一个有效搜索词，每个词不少于两个字符')
        return
      }
      if (!Number(this.soundId)) {
        const existing = new Set(this.searchTerms.map(item => item.term.toLowerCase()))
        terms.forEach(term => {
          if (existing.has(term.toLowerCase())) return
          this.draftTermSequence += 1
          this.searchTerms.push({
            id: `draft-${this.draftTermSequence}`,
            term,
            source: 'manual',
            draft: true
          })
          existing.add(term.toLowerCase())
        })
        this.newSearchTerms = ''
        return
      }

      this.savingTerms = true
      try {
        const response = await apis.tag.saveSearchTerms({
          sound_id: Number(this.soundId),
          terms
        })
        if (response.data.code !== 1) {
          throw new Error(response.data.msg || '添加搜索词失败')
        }
        this.newSearchTerms = ''
        await this.reloadDetail()
        this.$message.success(response.data.msg || '搜索词已添加')
        this.$emit('changed', { soundId: Number(this.soundId), type: 'terms' })
      } catch (error) {
        this.$message.error(String(error.message || error || '添加搜索词失败'))
      } finally {
        this.savingTerms = false
      }
    },
    /**
     * 删除本地草稿或经确认后删除已经保存的搜索词。
     *
     * @param {Object} term 搜索词记录
     * @returns {Promise<void>}
     */
    async removeSearchTerm(term) {
      if (term.draft || !Number(this.soundId)) {
        this.searchTerms = this.searchTerms.filter(item => item.id !== term.id)
        return
      }
      this.savingTerms = true
      try {
        await this.$confirm(
          `删除搜索词“${term.term}”后，用户输入这个词时可能无法再找到该音效。确定删除吗？`,
          '删除搜索词',
          { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
        )
        const response = await apis.tag.deleteSearchTerm({ id: term.id })
        if (response.data.code !== 1) {
          throw new Error(response.data.msg || '删除失败')
        }
        await this.reloadDetail()
        this.$message.success('搜索词已删除')
        this.$emit('changed', { soundId: Number(this.soundId), type: 'terms' })
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(String(error.message || error || '删除失败'))
        }
      } finally {
        this.savingTerms = false
      }
    },
    /**
     * 重新读取当前音效的标签和搜索词最终状态。
     *
     * @returns {Promise<void>}
     */
    async reloadDetail() {
      const response = await apis.tag.soundDetail({ sound_id: Number(this.soundId) })
      if (response.data.code !== 1) {
        throw new Error(response.data.msg || '音效整理信息刷新失败')
      }
      const detail = response.data.data || {}
      this.loadedSound = detail.sound || {}
      this.tagIds = (detail.tag_ids || []).map(Number)
      this.tagRelevanceScores = this.buildTagRelevanceMap(detail.relations)
      this.searchTerms = detail.search_terms || []
    },
    /**
     * 新增音效成功后，把本地标签和搜索词草稿关联到指定 soundId。
     *
     * @param {number|string} soundId 新音效 ID
     * @returns {Promise<boolean>}
     */
    async persistDraft(soundId) {
      const id = Number(soundId)
      if (!id) throw new Error('新音效 ID 不正确，无法保存标签和搜索词')
      if (this.tagIds.length) await this.saveTags(id, true)
      const terms = [
        ...this.searchTerms
        .filter(item => item.draft)
        .map(item => item.term),
        ...this.normalizeInputTerms(this.newSearchTerms)
      ]
      if (terms.length) {
        const response = await apis.tag.saveSearchTerms({
          sound_id: id,
          terms: Array.from(new Set(terms))
        })
        if (response.data.code !== 1) {
          throw new Error(response.data.msg || '搜索词保存失败')
        }
      }
      return true
    },
    /**
     * 将搜索词来源编码转换成工作人员可理解的中文。
     *
     * @param {string} source 来源编码
     * @returns {string}
     */
    termSourceLabel(source) {
      const labels = {
        manual: '人工添加',
        legacy_description: '旧关键词迁移',
        search_demand: '搜索日志',
        find_request: '找音效记录'
      }
      return labels[source] || source || '未知来源'
    },
    /**
     * 根据搜索词来源返回 Element 标签样式。
     *
     * @param {string} source 来源编码
     * @returns {string}
     */
    termSourceType(source) {
      if (source === 'manual') return 'success'
      if (source === 'search_demand' || source === 'find_request') return 'warning'
      return 'info'
    }
  }
}
</script>

<style lang="scss" scoped>
.sound-tag-search-editor {
  color: #2c3950;
}

.editor-section {
  padding: 19px;
  border: 1px solid #e3e9f1;
  border-radius: 12px;
  background: #fff;

  & + & { margin-top: 16px; }
}

.editor-heading {
  display: flex;
  margin-bottom: 15px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  h3 { margin: 0; color: #26344b; font-size: 16px; }
  p {
    margin: 6px 0 0;
    color: #8793a5;
    font-size: 12px;
    line-height: 1.55;
  }
}

.guide-progress {
  margin-top: 14px;
  padding: 13px 15px;
  border-radius: 9px;
  background: #f6f8fc;

  > div {
    display: flex;
    margin-bottom: 9px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  strong { color: #35445d; font-size: 13px; }
  span { color: #8b96a8; font-size: 12px; }
}

.guide-card {
  margin-top: 12px;
  padding: 17px;
  border: 1px solid #dce5f4;
  border-radius: 11px;
  background: linear-gradient(145deg, #fbfdff, #f5f8ff);
}

.guide-question {
  display: flex;
  margin-bottom: 14px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;

  h4 { margin: 5px 0 0; color: #23324a; font-size: 18px; }
  p { margin: 7px 0 0; color: #6f7d92; font-size: 13px; line-height: 1.6; }
}
.guide-step {
  color: #356df3;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.selected-count {
  flex: 0 0 auto;
  padding: 4px 9px;
  border-radius: 12px;
  background: #edf3ff;
  color: #356df3;
  font-size: 12px;
}
.guide-actions {
  display: flex;
  margin-top: 17px;
  align-items: center;
  gap: 8px;
}
.guide-spacer { flex: 1; }
.clear-group-button { color: #e25757; }

.quick-tip {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f6f8fc;
  color: #738096;
  font-size: 12px;

  i { margin-right: 5px; color: #ef8b32; }
}
.collapse-title {
  margin-right: 9px;
  color: #2c3950;
  font-weight: 600;
}
.collapse-description {
  margin-left: 10px;
  overflow: hidden;
  color: #98a2b1;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-summary {
  margin-top: 15px;
  padding: 13px;
  border-radius: 9px;
  background: #f7f9fc;
}
.selected-summary-heading {
  display: flex;
  margin-bottom: 9px;
  align-items: flex-start;
  justify-content: space-between;
  color: #66748a;
  font-size: 12px;

  p {
    margin: 4px 0 0;
    color: #929dad;
    font-weight: 400;
  }

  .relevance-scale-help {
    color: #a2abb8;
    font-size: 11px;
  }
}
.selected-tags {
  display: flex;
  flex-wrap: wrap;
  margin: -4px;
}
.selected-tag-item {
  display: flex;
  min-width: 330px;
  margin: 4px;
  align-items: center;
  padding: 6px 8px 6px 10px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  background: #fff;
}
.selected-tag-name {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;

  small {
    flex: 0 0 auto;
    margin-right: 6px;
    color: #8b97a9;
  }

  span {
    overflow: hidden;
    color: #40536d;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.relevance-editor {
  display: flex;
  flex: 0 0 auto;
  margin-left: 10px;
  align-items: center;
}
.relevance-level {
  width: 44px;
  margin-right: 7px;
  padding: 2px 0;
  border-radius: 8px;
  color: #738096;
  font-size: 11px;
  line-height: 16px;
  text-align: center;
  background: #eef2f7;
}
.relevance-level--core {
  color: #c14545;
  background: #fff0f0;
}
.relevance-level--important {
  color: #b56c19;
  background: #fff5e8;
}
.relevance-level--related {
  color: #3577b9;
  background: #edf6ff;
}
.relevance-level--weak {
  color: #69788e;
  background: #f0f3f7;
}
.relevance-level--minimal {
  color: #929ba8;
  background: #f5f6f8;
}
.relevance-input {
  width: 92px;
}
.remove-tag-button {
  margin-left: 5px;
  padding: 6px;
  color: #9ba5b3;

  &:hover { color: #e25757; }
}
.empty-copy { color: #9aa4b3; font-size: 12px; }
.section-save-row,
.term-add-row {
  display: flex;
  margin-top: 12px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  color: #8a96a8;
  font-size: 12px;
}
.term-list { margin-top: 14px; border-top: 1px solid #edf0f4; }
.term-item {
  display: flex;
  min-height: 46px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #edf0f4;
}
.term-main { display: flex; min-width: 0; align-items: center; gap: 8px; }
.danger-text { color: #e25757; }
.legacy-section { background: #fafbfd; }
.legacy-description {
  max-height: 120px;
  overflow: auto;
  color: #67758a;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 760px) {
  .editor-heading,
  .guide-progress > div,
  .guide-question,
  .section-save-row,
  .term-add-row {
    align-items: stretch;
    flex-direction: column;
  }
  .guide-actions { flex-wrap: wrap; }
  .guide-spacer { display: none; }
  .selected-tag-item {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }
}
</style>
